import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiZap, FiTarget, FiTrendingUp, FiActivity, FiAward } = FiIcons;

const HolographicStreakChart = ({ data = [], title = "Streak Performance" }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [animatedData, setAnimatedData] = useState([]);
  const [isScanning, setIsScanning] = useState(true);

  // DreamForge theme colors for streaks
  const STREAK_COLORS = [
    { hex: '#FED40B', rgb: '254,212,11', name: 'Forge Gold' },
    { hex: '#22c55e', rgb: '34,197,94', name: 'Success Green' },
    { hex: '#8b5cf6', rgb: '139,92,246', name: 'Vision Purple' },
    { hex: '#f59e0b', rgb: '245,158,11', name: 'Warning Amber' },
    { hex: '#06b6d4', rgb: '6,182,212', name: 'Info Cyan' },
    { hex: '#ef4444', rgb: '239,68,68', name: 'Danger Red' },
  ];

  // Process data for animation
  useEffect(() => {
    if (data.length > 0) {
      const initialData = data.map((item, index) => ({
        ...item,
        animatedStreak: 0,
        animatedTarget: 0,
        color: STREAK_COLORS[index % STREAK_COLORS.length],
        glowIntensity: 0,
        scanProgress: 0,
      }));

      setAnimatedData(initialData);

      // Stagger animation for dramatic effect
      data.forEach((_, index) => {
        setTimeout(() => {
          setAnimatedData(prevData =>
            prevData.map((item, i) =>
              i === index ? {
                ...item,
                animatedStreak: item.streak,
                animatedTarget: item.target,
                glowIntensity: 1,
                scanProgress: 100
              } : item
            )
          );
        }, index * 200);
      });
    }
  }, [data]);

  // Scanning effect controller
  useEffect(() => {
    const scanInterval = setInterval(() => {
      setIsScanning(prev => !prev);
    }, 3000);

    return () => clearInterval(scanInterval);
  }, []);

  // Custom tooltip for DreamForge theme
  const DreamForgeTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="relative bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-xl p-4 rounded-2xl border border-forge-400/50 shadow-dominance"
          style={{
            boxShadow: `
              0 0 20px rgba(254,212,11,0.4),
              inset 0 0 15px rgba(254,212,11,0.1)
            `
          }}
        >
          {/* DreamForge brackets */}
          <div className="absolute top-1 left-1 w-3 h-3 border-t border-l border-forge-400 opacity-80"></div>
          <div className="absolute top-1 right-1 w-3 h-3 border-t border-r border-forge-400 opacity-80"></div>
          <div className="absolute bottom-1 left-1 w-3 h-3 border-b border-l border-forge-400 opacity-80"></div>
          <div className="absolute bottom-1 right-1 w-3 h-3 border-b border-r border-forge-400 opacity-80"></div>

          <div className="flex items-center space-x-2 mb-3">
            <div className="text-2xl">{data.icon}</div>
            <p className="font-bold text-forge-400 tracking-wide">
              [{label}]
            </p>
          </div>

          <div className="space-y-1 text-xs">
            <div className="flex justify-between items-center text-forge-300">
              <span className="text-forge-400">&gt;CURRENT:</span>
              <span className="text-white font-bold">{data.streak} days</span>
            </div>
            <div className="flex justify-between items-center text-forge-300">
              <span className="text-forge-400">&gt;TARGET:</span>
              <span className="text-white font-bold">{data.target} days</span>
            </div>
            <div className="flex justify-between items-center text-forge-300">
              <span className="text-forge-400">&gt;PROGRESS:</span>
              <span className="text-forge-400 font-bold">{Math.round(data.completion)}%</span>
            </div>
            <div className="flex justify-between items-center text-forge-300">
              <span className="text-forge-400">&gt;STATUS:</span>
              <span className={`font-bold ${
                data.completion >= 100 ? 'text-success-400 animate-pulse' : 'text-warning-400'
              }`}>
                {data.completion >= 100 ? 'ACHIEVED' : 'IN PROGRESS'}
              </span>
            </div>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  const chartData = animatedData.length > 0 ? animatedData : [];

  return (
    <div className="relative bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl border border-forge-400/30 shadow-power overflow-hidden">
      <div className="p-3 sm:p-4 lg:p-6">
        {/* Header */}
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center space-x-3">
            <motion.div
              className="p-2 sm:p-3 bg-forge-gradient rounded-2xl shadow-lg"
              animate={{
                boxShadow: [
                  "0 0 15px rgba(254,212,11,0.3)",
                  "0 0 25px rgba(254,212,11,0.6)",
                  "0 0 15px rgba(254,212,11,0.3)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <SafeIcon icon={FiZap} className="text-shadow-950 text-lg sm:text-xl" />
            </motion.div>
            <div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-forge-400 tracking-wide">
                {title}
              </h3>
              <p className="text-forge-300 text-xs sm:text-sm opacity-80">
                Track your habit streaks and progress
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-xs text-forge-400"
            >
              {isScanning ? 'ANALYZING...' : 'ANALYSIS COMPLETE'}
            </motion.div>
          </div>
        </div>

        {/* Chart container */}
        <div className="relative z-10 h-[350px] sm:h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="horizontal"
              margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
            >
              <XAxis
                type="number"
                stroke="#FED40B"
                fontSize={12}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={80}
                stroke="#FED40B"
                fontSize={10}
              />

              {/* Target bars (background) */}
              <Bar
                dataKey="animatedTarget"
                fill="rgba(107,114,128,0.3)"
                radius={[0, 4, 4, 0]}
                isAnimationActive={true}
                animationDuration={1500}
              />

              {/* Streak bars (foreground) */}
              <Bar
                dataKey="animatedStreak"
                radius={[0, 8, 8, 0]}
                isAnimationActive={true}
                animationDuration={2000}
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color.hex}
                    stroke={hoveredIndex === index ? '#ffffff' : 'transparent'}
                    strokeWidth={hoveredIndex === index ? 2 : 0}
                    style={{
                      filter: hoveredIndex === index
                        ? `drop-shadow(0 0 10px ${entry.color.hex}) brightness(1.2)`
                        : `drop-shadow(0 0 5px ${entry.color.hex}52)`,
                      transition: 'all 0.3s ease'
                    }}
                  />
                ))}
              </Bar>

              {/* Custom tooltip */}
              <Tooltip
                content={<DreamForgeTooltip />}
                cursor={{ fill: 'rgba(254,212,11,0.1)' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend with performance indicators */}
        <div className="relative z-10 mt-4 space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-forge-400">PERFORMANCE METRICS:</span>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded"></div>
                <span className="text-gray-400">TARGET</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-forge-400 rounded"></div>
                <span className="text-forge-400">CURRENT</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {chartData.slice(0, 6).map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + index * 0.1 }}
                className="flex items-center justify-between bg-shadow-800/50 backdrop-blur-sm px-3 py-2 rounded-xl border border-forge-400/20 hover:border-forge-400/50 transition-all duration-300"
                style={{
                  boxShadow: hoveredIndex === index ? `0 0 15px rgba(${item.color.rgb},0.3)` : 'none'
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="flex items-center space-x-2">
                  <div className="text-sm">{item.icon}</div>
                  <span className="text-xs text-forge-300 truncate max-w-20">
                    {item.name}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-forge-400 font-bold">
                    {item.streak}/{item.target}
                  </span>
                  <div
                    className="w-2 h-2 rounded"
                    style={{ backgroundColor: item.color.hex }}
                  ></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* DreamForge border effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-forge-400 to-transparent opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-forge-400 to-transparent opacity-50"></div>
        <div className="absolute top-0 left-0 w-0.5 h-full bg-gradient-to-b from-transparent via-forge-400 to-transparent opacity-50"></div>
        <div className="absolute top-0 right-0 w-0.5 h-full bg-gradient-to-b from-transparent via-forge-400 to-transparent opacity-50"></div>
      </div>
    </div>
  );
};

export default HolographicStreakChart;